/**
 * Product Map Page
 *
 * A grouped sortable list (NOT a board): product areas as group rows with
 * capability rows beneath, cross-group drag-and-drop via one shared
 * `DndContext` (see `useProductMapDragDrop`), and a capability drawer.
 * Deliberately a first-class editor — every field is inline-editable and
 * "＋ capability" appends an already-editable row, so a user can bootstrap a
 * map by hand in minutes without waiting on the planner.
 *
 * @module features/product-map/pages/ProductMapPage
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { DndContext, DragOverlay, MeasuringStrategy } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Compass, Plus, Rows, TreeStructure } from '@phosphor-icons/react';
import {
  Button,
  DrawerTrigger,
  EmptyState,
  Flex,
  Button as HiddenButton,
  LoadingOrbit,
  Page,
  PageHeader,
  PillToggle,
  Text,
  type PillToggleOption,
} from '@chatsdet/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { useProductMapViewPrefs } from '../../../hooks/useUserSettings';
import { usePropertyBreadcrumbs } from '../../../hooks/usePropertyBreadcrumbs';
import { productMapRoute } from '../../../router';
import { ExploreRunNowAction, ExploreView, useExploreRunNow } from '../../exploration/components/ExploreView';
import { AreaRow } from '../components/AreaRow';
import { CapabilityDrawer } from '../components/CapabilityDrawer';
import { CapabilityRow } from '../components/CapabilityRow';
import { useProductMapDragDrop } from '../hooks/useProductMapDragDrop';
import { productMapCollisionDetection } from '../productMapCollision';
import type { ProductMapSearch, ProductMapViewMode } from '../productMapRouteSearch';
import { rollUpLinkedCapabilities } from '../productMapLogic';
import { useCreateArea, useCreateCapability, useProductMap } from '../useProductMap';

const VIEW_OPTIONS: readonly PillToggleOption<ProductMapViewMode>[] = [
  { value: 'list', label: 'List', icon: <Rows size={14} /> },
  { value: 'explore', label: 'Explore', icon: <Compass size={14} /> },
];

// The mid-drag splice in `useProductMapDragDrop`'s `onDragOver` changes
// column heights as neighbors shift to preview the drop — with dnd-kit's
// default `WhileDragging` measuring, droppable rects are captured once at
// drag start and go stale the moment that reflow happens, which is what made
// the collision result (and thus the preview) lag or resolve against the
// wrong rects mid-drag. Measuring `Always` re-measures on every frame so
// `productMapCollisionDetection` always sees current rects.
const MEASURING_CONFIG = { droppable: { strategy: MeasuringStrategy.Always } };

export function ProductMapPage() {
  const navigate = useNavigate();
  const search = productMapRoute.useSearch();
  const { user } = useAuth();
  const hasExploration = Boolean(user?.features?.exploration);
  const { viewMode: persistedView, setViewMode: setPersistedView } = useProductMapViewPrefs();
  const view: ProductMapViewMode = hasExploration ? search.view ?? persistedView : 'list';
  const { propertyId, selectProperty, propertiesQuery, activeProperty } = usePropertyBreadcrumbs({
    pageBreadcrumb: { key: 'product-map', label: 'Product Map' },
    // Keep `?propertyId=` in sync with the resolved selection so the
    // assistant (a pure router-state reader) can see which application is in
    // scope. `onPropertyChange` — not `onSelectProperty` — because it also
    // fires on localStorage-restore and first-item-fallback resolution, not
    // just explicit dropdown picks; `replace: true` so those automatic
    // resolutions don't push a history entry on every page load.
    onPropertyChange: (nextPropertyId) => {
      void navigate({
        to: '/product-map',
        replace: true,
        search: (s: ProductMapSearch) => ({ ...s, propertyId: nextPropertyId ?? undefined }),
      });
    },
  });

  // Seed the selection from the URL on mount so a shared/reloaded link with
  // `?propertyId=` wins over the stored selection once properties are known
  // (see `useOrgPropertySelection`'s `propertyId && validIds.has(propertyId)`
  // sync branch).
  useEffect(() => {
    if (search.propertyId) {
      selectProperty(search.propertyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewChange = (next: ProductMapViewMode) => {
    setPersistedView(next);
    void navigate({ to: '/product-map', search: { ...search, view: next } });
  };

  // Called unconditionally (hook rules) — `enabled` gates the underlying
  // queries so switching to List stops polling exploration cycles.
  const exploreRunNow = useExploreRunNow(propertyId, hasExploration && view === 'explore');

  const mapQuery = useProductMap(propertyId ?? undefined);
  const map = mapQuery.data;

  const createArea = useCreateArea(propertyId ?? '');
  const createCapability = useCreateCapability(propertyId ?? '');

  const [autoEditAreaId, setAutoEditAreaId] = useState<string | null>(null);
  const [autoEditCapabilityId, setAutoEditCapabilityId] = useState<string | null>(null);

  const dragDrop = useProductMapDragDrop(propertyId ?? '', map);

  const capabilityId = search.capability;
  const drawerOpen = Boolean(capabilityId);

  const handleDrawerOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      void navigate({ to: '/product-map', search: { ...search, capability: undefined } });
    }
  };

  const openCapability = (id: string) => {
    void navigate({ to: '/product-map', search: { ...search, capability: id } });
  };

  // Deep-link support for `?area=` — briefly highlight the target area's
  // row instead of a full scroll rig, kept intentionally simple.
  const [highlightAreaId, setHighlightAreaId] = useState<string | null>(null);
  useEffect(() => {
    if (search.area) {
      setHighlightAreaId(search.area);
      const timer = setTimeout(() => setHighlightAreaId(null), 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [search.area]);

  const allCapabilitiesById = useMemo(() => {
    const byId = new Map<string, ReturnType<typeof flattenAll>[number]>();
    for (const c of flattenAll(map)) byId.set(c.id, c);
    return byId;
  }, [map]);

  const handleAddArea = () => {
    if (!propertyId) return;
    createArea.mutate(
      { title: 'New area' },
      { onSuccess: (area) => setAutoEditAreaId(area.id) }
    );
  };

  const handleAddCapability = (areaId: string | null) => {
    if (!propertyId) return;
    createCapability.mutate(
      { areaId, title: 'New capability' },
      { onSuccess: (capability) => setAutoEditCapabilityId(capability.id) }
    );
  };

  const isEmpty = !mapQuery.isLoading && (map?.areas.length ?? 0) === 0 && (map?.unassignedCapabilities.length ?? 0) === 0;

  const linkRollup = useMemo(() => rollUpLinkedCapabilities(flattenAll(map)), [map]);
  const linkedPct =
    linkRollup.total > 0 ? Math.round((linkRollup.linked / linkRollup.total) * 100) : null;
  const blockedCount = useMemo(
    () => flattenAll(map).filter((c) => c.coverageStatus === 'blocked').length,
    [map]
  );

  const knownIssueCount = useMemo(
    () => flattenAll(map).filter((c) => c.coverageStatus === 'known_issue').length,
    [map]
  );

  return (
    <Page>
      <PageHeader
        title={activeProperty?.name ?? 'Product map'}
        description={
          map ? (
            // Honesty rule: never show a coverage figure without this strip —
            // the map only ever covers what a human or the planner has
            // actually mapped, so a bare percentage would overstate it.
            <Text size="sm" className="text-muted-foreground">
              {linkedPct !== null && (
                <>
                  {linkRollup.linked}/{linkRollup.total} capabilities linked ({linkedPct}%) ·{' '}
                </>
              )}
              {map.unmappedWorkflowCount} flow{map.unmappedWorkflowCount === 1 ? '' : 's'} unmapped
              {blockedCount > 0 && ` · ${blockedCount} blocked`}
              {knownIssueCount > 0 && ` · ${knownIssueCount} known issue${knownIssueCount === 1 ? '' : 's'}`}
            </Text>
          ) : undefined
        }
        actions={
          <Flex align="center" gap="sm">
            {hasExploration && (
              <PillToggle
                aria-label="Product map view"
                value={view}
                onChange={handleViewChange}
                options={VIEW_OPTIONS}
              />
            )}
            {propertyId && view === 'explore' ? (
              <ExploreRunNowAction state={exploreRunNow} />
            ) : propertyId && !isEmpty ? (
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<Plus size={14} />}
                onPress={handleAddArea}
                isDisabled={createArea.isPending}
              >
                Add area
              </Button>
            ) : undefined}
          </Flex>
        }
      />

      {!propertyId ? (
        <Flex align="center" justify="center" className="flex-1 py-16">
          {propertiesQuery.isLoading ? (
            <LoadingOrbit size="lg" />
          ) : (
            <Text className="text-muted-foreground">Select a property to view its product map.</Text>
          )}
        </Flex>
      ) : view === 'explore' ? (
        <ExploreView propertyId={propertyId} state={exploreRunNow} />
      ) : mapQuery.isLoading ? (
        <Flex align="center" justify="center" className="flex-1 py-16">
          <LoadingOrbit size="lg" />
        </Flex>
      ) : isEmpty ? (
        <EmptyState
          icon={<TreeStructure size={24} />}
          title="Map your product"
          description="Group related capabilities into areas — Checkout & Payments, Account Management — so coverage is legible at a glance. Add your first area by hand, or wait for the planner to populate one."
          action={
            <Button variant="primary" iconLeft={<Plus size={16} />} onPress={handleAddArea} isDisabled={createArea.isPending}>
              Add your first area
            </Button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card divide-y divide-border">
          <DndContext
            sensors={dragDrop.sensors}
            collisionDetection={productMapCollisionDetection}
            measuring={MEASURING_CONFIG}
            onDragStart={dragDrop.handleDragStart}
            onDragOver={dragDrop.handleDragOver}
            onDragEnd={dragDrop.handleDragEnd}
            onDragCancel={dragDrop.handleDragCancel}
          >
            <SortableContext items={dragDrop.areaIds} strategy={verticalListSortingStrategy}>
              {dragDrop.areasForRender.map((area) => (
                <AreaRow
                  key={area.id}
                  area={area}
                  capabilities={area.capabilities}
                  onOpenCapability={openCapability}
                  onAddCapability={handleAddCapability}
                  autoEditCapabilityId={autoEditCapabilityId}
                  autoEditTitle={area.id === autoEditAreaId}
                  highlighted={highlightAreaId === area.id}
                />
              ))}

              <AreaRow
                area={null}
                capabilities={dragDrop.unassignedForRender}
                onOpenCapability={openCapability}
                onAddCapability={handleAddCapability}
                autoEditCapabilityId={autoEditCapabilityId}
              />
            </SortableContext>

            <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
              {dragDrop.activeDrag?.type === 'capability' && allCapabilitiesById.get(dragDrop.activeDrag.id) ? (
                <CapabilityRow
                  capability={allCapabilitiesById.get(dragDrop.activeDrag.id)!}
                  onOpen={() => {}}
                  isOverlay
                />
              ) : dragDrop.activeDrag ? (
                <div className="rounded-md border border-primary/30 bg-card px-3 py-1.5 text-sm shadow-lg">
                  {dragDrop.activeDrag.label ?? 'Dragging…'}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      )}

      <DrawerTrigger isOpen={drawerOpen} onOpenChange={handleDrawerOpenChange}>
        <HiddenButton className="hidden">Open</HiddenButton>
        {capabilityId ? <CapabilityDrawer capabilityId={capabilityId} /> : <div />}
      </DrawerTrigger>
    </Page>
  );
}

function flattenAll(map: ReturnType<typeof useProductMap>['data']) {
  if (!map) return [];
  return [...map.areas.flatMap((area) => area.capabilities), ...map.unassignedCapabilities];
}
